/**
 * GalleryInstanceStore
 *
 * 每个 GalleryListWrapper 实例创建一个独立的 GalleryInstanceStore，
 * 替代全局 galleryStore，解决同页面多个 GalleryListWrapper 共享状态的冲突问题。
 *
 * 该 store 复制了 GalleryStore 中与照片列表渲染相关的全部状态和方法，
 * 但作为独立实例运行，不再共享全局单例。
 */
//  @ts-nocheck
import { observable, action } from "mobx";
import galleryApi from "../../actions/gallery";
import imageApi, { IImageListResult } from "../../actions/image";
import { IOneGallery } from "./Common";
import { ICheckLoadMore } from "../Common/ListWrapper";
import RootStore from "../../store/RootStore";
import { MessageProps } from "../../store/IMStore";

export interface ILoadPicParams {
  first?: boolean;
  uselasttime?: boolean;
  notmerge?: boolean;
  orderchange?: boolean;
  tag?: string;
  person?: boolean;
  page?: number;
  hot?: boolean;
  galleryids?: string[];
  sortType?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface IJigsaw {
  bottomShow: boolean;
  jigsawList: IOneGallery[];
  type: string;
  from?: string;
}

export default class GalleryInstanceStore {
  rootStore: RootStore;
  private loadPicCount = 0;
  private _randomTimer = 0;

  // 组件元数据：用于 reload() 路由到正确的加载方法
  _componentType: string = 'photo';
  _galleryIds: string[] = [];
  _componentTag: string = '';
  _componentSortType: string = '';
  _componentSortOrder: 'asc' | 'desc' | '' = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * 
   * @param type 这儿是在初始化的时候设置组件的元信息, 主要是为了 reload 的时候知道应该调用 loadPic 还是 loadTagPic, 反正就是
   * 为了避免后面不知道页面的组件是啥, 乱调用接口
   * @param galleryIds 
   * @param tag 
   */
  @action setComponentMeta = (
    type: string,
    galleryIds?: string[],
    tag?: string,
    sortType?: string,
    sortOrder?: 'asc' | 'desc'
  ) => {
    this._componentType = type || 'photo';
    this._galleryIds = galleryIds || [];
    this._componentTag = tag || '';
    this._componentSortType = sortType || '';
    this._componentSortOrder = sortOrder || '';
  };

  getTagSortMeta = (sortType?: string, sortOrder?: 'asc' | 'desc') => {
    const rawSortType = sortType || this._componentSortType || '';
    const rawSortOrder = sortOrder || this._componentSortOrder || 'desc';

    if (rawSortType === 'hotIndex' || rawSortType === 'index') {
      return {
        sorttype: 'index',
        order: rawSortOrder || 'desc',
      };
    }

    if (rawSortType === 'uploadTime' || rawSortType === 'upload_time' || rawSortType === 'uploadtime') {
      return {
        sorttype: 'uploadtime',
        order: rawSortOrder || 'desc',
      };
    }

    return {
      sorttype: 'shoottime',
      order: rawSortOrder || 'desc',
    };
  };

  /**
   * 统一的重新加载方法，根据 _componentType 自动路由到 loadPic 或 loadTagPic。
   * pendant 组件（刷新、排序等）应调用此方法而非直接调用 loadPic。
   */
  reload = (params: Partial<ILoadPicParams> = {}) => {
    if (this._componentType === 'tagphoto') {
      const tagSortMeta = this.getTagSortMeta(params.sortType, params.sortOrder);
      return this.loadTagPic({
        ...params,
        galleryids: this._galleryIds,
        tag: params.tag !== undefined ? params.tag : this._componentTag,
        sortType: tagSortMeta.sorttype,
        sortOrder: tagSortMeta.order,
      });
    }
    return this.loadPic(params);
  };

  // =================== Observable State ===================

  @observable hotList = [] as IOneGallery[];
  @observable jigsaw = {
    bottomShow: false,
    jigsawList: [],
    type: "",
  } as IJigsaw;
  @observable hot = false;
  @observable personList = [] as any[];
  @observable list = [] as IOneGallery[];
  @observable total = -1;

  morePhotoCount = 0;
  @observable morePhoto = 0;

  @observable listIsPerson = false;
  @observable personPage = 1;

  @observable mode = "1";
  @observable tag = "";
  @observable order = (() => {
    let result =
      (window.configDetail?.sort || "").indexOf("-") === 0 ? "desc" : "asc";
    return result;
  })();

  hideList = [] as string[];
  uncheckList = [] as string[];

  @observable watermarks = {} as any;
  @observable payGoods = {} as any;
  @observable disableWatermark = false;

  // =================== Actions ===================

  @action clearMorePhoto = () => {
    this.morePhoto = 0;
    this.morePhotoCount = 0;
    window.clearTimeout(this._randomTimer);
  };

  randomSetMorePhoto = () => {
    window.clearTimeout(this._randomTimer);
    this._randomTimer = window.setTimeout(
      action(() => {
        this.morePhoto = this.morePhotoCount;
      }),
      Math.random() * 2000 + 1000
    );
  };

  @action filterPhotoList = () => {
    this.list = this.list.filter((it: IOneGallery) => {
      let { id } = it;
      if (this.hideList.indexOf(String(id)) > -1) {
        return false;
      }
      if (this.uncheckList.indexOf(String(id)) > -1) {
        return false;
      }
      return true;
    });
  };

  @action setDisableWatermark = (val: boolean) => {
    this.disableWatermark = val;
  };

  @action setJigsawBottom = (flag: boolean) => {
    this.jigsaw = { ...this.jigsaw, bottomShow: flag };
  };

  @action setJigsaw = (params: {
    bottomShow?: boolean;
    jigsawList?: IOneGallery[];
    type?: string;
    extra?: string;
    from?: string;
  }) => {
    this.jigsaw = { ...this.jigsaw, ...params };
  };

  @action setList = (list: IOneGallery[]) => {
    const theList = list.map(function (it: any, ind: number) {
      if (it.ind === undefined) {
        it.ind = ind;
      }
      return it;
    });
    if (this.hot) {
      this.hotList = theList;
    } else {
      this.list = theList;
    }
    setTimeout(() => {
      window.dispatchEvent(new Event("custom-tab-height-change"));
    }, 0);
  };

  @action setTotal = (total: number) => {
    this.total = total;
  };

  @action setListIsPerson = (flag: boolean) => {
    this.listIsPerson = flag;
  };

  @action setPersonPage = (page: number) => {
    this.personPage = page;
  };

  @action setHot = (hot: boolean) => {
    this.hot = hot;
  };

  @action setMode = (mode: string) => {
    this.mode = mode;
  };

  @action setTag = (tag: string) => {
    this.tag = tag;
  };

  @action setOrder = (order?: string) => {
    if (order) {
      this.order = order;
    } else {
      this.order = this.order === "desc" ? "asc" : "desc";
    }
  };

  getWatermarksByKey = (key: string, show_mark?: number) => {
    if (window.original_photo) {
      return [];
    }
    if (Number(show_mark) === 0) {
      return [];
    }
    if (window.custom_current_watermark) {
      return window.custom_current_watermark;
    }
    return this.watermarks[key];
  };

  @action setWatermarksByKey = (key: string, watermark: any) => {
    if (typeof watermark == "string" && watermark) {
      try {
        watermark = JSON.parse(watermark);
      } catch (e) {
        return;
      }
    }
    if (watermark && !this.watermarks[key]) {
      this.watermarks[key] = watermark;
    }
  };

  getPayGoodByKey = (key: string) => {
    return Number(this.payGoods[key]);
  };

  @action setPayGoodByKey = (key: string, goodId: any) => {
    if (goodId && !this.payGoods[key]) {
      this.payGoods[key] = goodId;
    }
  };

  // =================== API Methods ===================

  getPersonList = (galleryid: string, page: number = 1) => {
    return galleryApi.person(galleryid, page).then(
      action((res: any) => {
        this.personList = page === 1 ? res : [...this.personList, ...res];
        setTimeout(() => {
          window.dispatchEvent(new Event("custom-tab-height-change"));
        }, 0);
        const total = res.length < 35 ? res.length : Infinity;
        return { data: res, total };
      })
    );
  };

  getHotList = (galleryid: string, page: number) => {
    return galleryApi.imagesHot(galleryid, page).then(
      action((res: any) => {
        const { code, data } = res;
        if (code == 200) {
          let { items, total } = data;
          this.setTotal(total);
          this.hotList = page === 1 ? items : [...this.hotList, ...items];
          return { data: items, total };
        } else {
          console.warn("hot list获取失败", res);
        }
        setTimeout(() => {
          window.dispatchEvent(new Event("custom-tab-height-change"));
        }, 0);
        return { data: [], total: 0 };
      })
    );
  };

  loadTagPic = (obj: ILoadPicParams) => {
    const {
      first = false,
      uselasttime,
      notmerge,
      orderchange,
      tag,
      galleryids,
    } = obj;

    if (!galleryids || !galleryids.length) {
      this.setList([]);
      this.setTotal(0);
      return Promise.resolve({ data: [], total: 0 });
    }

    if (first || notmerge) {
      this.loadPicCount = 0;
      this.clearMorePhoto();
      this.hideList = [];
      this.uncheckList = [];
    }

    this.setListIsPerson(false);
    this.setHot(false);

    if (tag !== undefined) {
      this.setTag(tag);
    }
    if (this.loadPicCount > 3) {
      console.warn("sth error happened here, loadPicCount too high", this.loadPicCount);
      return;
    }

    const tagSortMeta = this.getTagSortMeta(obj.sortType, obj.sortOrder);
    let sorttype = tagSortMeta.sorttype;

    let lasttime = null;
    let offset = 0;
    const { list } = this;

    if (true === uselasttime) {
      if (list && list.length) {
        const last = list[list.length - 1];
        lasttime = last[sorttype || "shoottime"];
        offset = last.id;
      }
    }
    if (true === orderchange) {
      lasttime = null;
    }

    let order = tagSortMeta.order || this.order;
    return imageApi
      .tagList({
        sorttype,
        tag: this.tag,
        order,
        lasttime,
        offset,
        galleryid: Number(window.gallery?.id),
        galleryids: galleryids,
      })
      .then(
        action((jsonData: IImageListResult) => {
          let format_list = jsonData.data;
          const { total } = jsonData;
          if (format_list && format_list.length == 0) {
            this.loadPicCount++;
          }
          let newList =
            notmerge || first ? format_list : list.concat(format_list);
          this.setList(
            newList
              .map((it: IOneGallery, index: number) => {
                it.index = index + 1;
                return it;
              })
              .filter((it: IOneGallery) => {
                let { id } = it;
                if (this.hideList.indexOf(String(id)) > -1) return false;
                if (this.uncheckList.indexOf(String(id)) > -1) return false;
                return true;
              })
          );
          this.setTotal(total);

          if (window.dataDetail && window.dataDetail.id) {
            this.rootStore.pagedataStore!.updateGalleryCount(
              String(window.dataDetail!.id),
              total
            );
          }
          return { data: newList, total };
        })
      );
  };

  loadPic = (obj: ILoadPicParams) => {
    const {
      first = false,
      uselasttime,
      notmerge,
      orderchange,
      tag,
      person,
      hot,
      page,
    } = obj;

    if (first || notmerge) {
      this.loadPicCount = 0;
      this.clearMorePhoto();
      this.hideList = [];
      this.uncheckList = [];
    }
    if (page) {
      this.setPersonPage(page);
    }
    if (typeof person === "boolean") {
      this.setListIsPerson(person);
    }
    if (typeof hot === "boolean") {
      this.setHot(hot);
    }
    if (tag) {
      this.setListIsPerson(false);
      this.setHot(false);
    }
    if (this.listIsPerson || this.hot) {
      this.setTag("");
      if (uselasttime) {
        this.setPersonPage(this.personPage + 1);
      } else if (notmerge) {
        this.setPersonPage(1);
      }
    }
    if (tag !== undefined) {
      this.setTag(tag);
    }
    if (this.loadPicCount > 3) {
      console.warn("sth error happened here, loadPicCount too high", this.loadPicCount);
      return;
    }

    let sorttype = window.configDetail?.sort || "-shoottime";
    sorttype = sorttype.replace(/(\+|-)/, "");

    let lasttime = null;
    let offset = 0;
    const { list } = this;

    if (true === uselasttime) {
      if (list && list.length) {
        const last = list[list.length - 1];
        lasttime = last[sorttype || "shoottime"];
        offset = last.id;
      }
    }
    if (true === orderchange) {
      lasttime = null;
    }

    let order = this.order;
    // 这个地方有点问题, 主要是如果tabs中的, 这儿应该是多少呢?
    if (window.dataDetail && window.dataDetail.id) {
      if (this.hot) {
        return this.getHotList(window.dataDetail.id + "", this.personPage);
      }
      if (this.listIsPerson) {
        return this.getPersonList(window.dataDetail.id + "", this.personPage);
      }
      return imageApi
        .list({
          galleryid: window.dataDetail.id,
          sorttype,
          tag: this.tag,
          order,
          lasttime,
          offset,
        })
        .then(
          action((jsonData: IImageListResult) => {
            let format_list = jsonData.data;
            const { total } = jsonData;
            if (format_list && format_list.length == 0) {
              this.loadPicCount++;
            }
            let newList =
              notmerge || first ? format_list : list.concat(format_list);
            this.setList(
              newList
                .map((it: IOneGallery, index: number) => {
                  it.index = index + 1;
                  return it;
                })
                .filter((it: IOneGallery) => {
                  let { id } = it;
                  if (this.hideList.indexOf(String(id)) > -1) return false;
                  if (this.uncheckList.indexOf(String(id)) > -1) return false;
                  return true;
                })
            );
            this.setTotal(total);

            if (window.dataDetail && window.dataDetail.id) {
              this.rootStore.pagedataStore!.updateGalleryCount(
                String(window.dataDetail!.id),
                total
              );
            }
            return { data: newList, total };
          })
        );
    } else {
      return new Promise<ICheckLoadMore>((resolve) =>
        resolve({ data: [], total: 0 })
      );
    }
  };

  // =================== IM Message Handling ===================

  onMessage = (data: MessageProps) => {
    if (data) {
      let { type, current_gallery, image_id } = data;

      let rightGalleryId = window.dataDetail
        ? window.dataDetail.id
        : window.gallery?.id;

      if (rightGalleryId == current_gallery) {
        if (type == "image_add") {
          let currentTime = 0;
          if (typeof image_id == "string") {
            currentTime = 1;
          }
          if (image_id instanceof Array) {
            currentTime = image_id.length;
          }
          this.morePhotoCount += Number(currentTime);
          this.randomSetMorePhoto();
        }
        if (type == "image_delete") {
          this.list = this.list.filter((it: IOneGallery) => {
            if (typeof image_id == "string") {
              return Number(it.id) != Number(image_id);
            } else if (image_id instanceof Array) {
              return image_id.indexOf(Number(it.id)) < 0;
            }
          });
        }
        if (type == "image_hide") {
          if (image_id instanceof Array) {
            image_id.forEach((it) => {
              if (this.hideList.indexOf(String(it)) < 0) {
                this.hideList.push(String(it));
              }
            });
          } else {
            if (this.hideList.indexOf(image_id) < 0) {
              this.hideList.push(image_id);
            }
          }
          this.filterPhotoList();
        }
        if (type == "image_show") {
          this.hideList = this.hideList.filter((it: string) => {
            if (typeof image_id == "string") {
              return Number(it) != Number(image_id);
            } else if (image_id instanceof Array) {
              return image_id.indexOf(String(it)) < 0;
            }
          });
          this.filterPhotoList();
        }
        if (type == "image_check") {
          this.uncheckList = this.uncheckList.filter((it: string) => {
            if (typeof image_id == "string") {
              return Number(it) != Number(image_id);
            } else if (image_id instanceof Array) {
              return image_id.indexOf(String(it)) < 0;
            }
          });
          this.filterPhotoList();
        }
        if (type == "image_uncheck") {
          if (image_id instanceof Array) {
            image_id.forEach((it) => {
              if (this.uncheckList.indexOf(String(it)) < 0) {
                this.uncheckList.push(String(it));
              }
            });
          } else {
            if (this.uncheckList.indexOf(image_id) < 0) {
              this.uncheckList.push(image_id);
            }
          }
          this.filterPhotoList();
        }
      }
    }
  };

  private _imTypes = [
    "image_add",
    "image_delete",
    "image_show",
    "image_hide",
    "image_check",
    "image_uncheck",
  ];

  registerIMListeners = () => {
    const imStore = this.rootStore.imStore;
    if (imStore) {
      this._imTypes.forEach((type) => {
        imStore.addMessageListener(type, this.onMessage);
      });
    }
  };

  unregisterIMListeners = () => {
    const imStore = this.rootStore.imStore;
    if (imStore) {
      this._imTypes.forEach((type) => {
        imStore.removeMessageListener(type, this.onMessage);
      });
    }
  };
}
